import {Color} from '@benchling/nucleus-ui';
import {UnstyledButton, createStyles} from '@mantine/core';
import React from 'react';

import {AI_CHAT_ACCENT_COLOR} from 'omnibox/omniboxConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import type {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

const useStyles = createStyles(() => ({
  root: {
    paddingBottom: 0,
  },
  sectionHeader: {
    padding: '6px 16px',
    fontSize: 11,
    fontWeight: 700,
    lineHeight: '16.5px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.55px',
    // fg-dimmed per Figma, matching the "Recent Searches" header.
    color: '#656566',
  },
  cardsRow: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: 13,
    padding: '0 16px 16px',
  },
  cardIcon: {
    width: 34,
    height: 34,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    // Driven by the parent card's CSS variable so the tint can shift on card hover.
    backgroundColor: 'var(--qa-icon-bg)',
  },
  cardIconGlyph: {
    fontSize: 14,
  },
  cardInner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    width: '100%',
    height: 36,
  },
  cardTextWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
    overflow: 'hidden',
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 700,
    lineHeight: '16px',
    color: Color.GRAY_900,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardSubtitle: {
    fontSize: 12,
    fontWeight: 400,
    color: Color.GRAY_900,
    lineHeight: '14px',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

interface QuickActionCardProps {
  icon: BenchlingIconName;
  iconBg: string;
  iconBgHover: string;
  iconColor: string;
  title: string;
  subtitle: string;
  onClick: () => void;
}

function QuickActionCard({
  icon,
  iconBg,
  iconBgHover,
  iconColor,
  title,
  subtitle,
  onClick,
}: QuickActionCardProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <UnstyledButton
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        flex: 1,
        minWidth: 0,
        height: 60,
        padding: 12,
        background: Color.WHITE,
        borderRadius: 10,
        border: `1px solid ${Color.GRAY_300}`,
        transition: 'all 100ms ease',
        '--qa-icon-bg': iconBg,
        '&:hover': {
          backgroundColor: Color.GRAY_50,
          borderColor: Color.GRAY_400,
          '--qa-icon-bg': iconBgHover,
        },
      }}
    >
      <div className={classes.cardInner}>
        <div className={classes.cardIcon} style={{color: iconColor}} aria-hidden="true">
          <BenchlingIcon icon={icon} className={classes.cardIconGlyph} />
        </div>
        <div className={classes.cardTextWrapper}>
          <div className={classes.cardTitle}>{title}</div>
          <div className={classes.cardSubtitle}>{subtitle}</div>
        </div>
      </div>
    </UnstyledButton>
  );
}

interface OmniboxQuickActionsProps {
  composeEnabled: boolean;
  onEntryFromCompose: () => void;
  onCreateEntry: () => void;
  onOpenReviews: () => void;
  onViewRecentEntries: () => void;
}

export default function OmniboxQuickActions({
  composeEnabled,
  onEntryFromCompose,
  onCreateEntry,
  onOpenReviews,
  onViewRecentEntries,
}: OmniboxQuickActionsProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={classes.root}>
      <div className={classes.sectionHeader}>Quick Actions</div>
      <div className={classes.cardsRow}>
        <QuickActionCard
          icon="clock-rotate-left"
          iconBg="#e1f0ff"
          iconBgHover="#c7e0ff"
          iconColor="#1a73e8"
          title="View recent entries"
          subtitle="Pick up where you left off"
          onClick={onViewRecentEntries}
        />
        <QuickActionCard
          icon="workflow"
          iconBg="#ffedd9"
          iconBgHover="#ffd9a8"
          iconColor="#d9822b"
          title="View open tasks"
          subtitle="Items that need your review"
          onClick={onOpenReviews}
        />
        {composeEnabled ? (
          <QuickActionCard
            icon="wand-magic-sparkles"
            iconBg="#f1e9ff"
            iconBgHover="#e2cfff"
            iconColor={AI_CHAT_ACCENT_COLOR}
            title="Start new AI chat"
            subtitle="Prompt-based creation"
            onClick={onEntryFromCompose}
          />
        ) : (
          <QuickActionCard
            icon="book"
            iconBg="#e7edff"
            iconBgHover="#c9d5ff"
            iconColor="#3b5bfd"
            title="Create new Entry"
            subtitle="Start a new experiment"
            onClick={onCreateEntry}
          />
        )}
      </div>
    </div>
  );
}
